import { Box, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import './styles/filterPanel.css';

/**
 * Layout helpers shared across the filter system.
 *
 * Why a separate file?
 * - `FieldLabel` is used by every field strategy (Text, Date, Numeric, Boolean)
 *   to render the label above the input in panel variant. Centralising it means
 *   one place to change the typography/style for all fields at once.
 * - `SectionLabel` is used by FilterPanel to render grouped section headers.
 *   It lives here so FilterPanel can import it via FilterField's re-export
 *   without reaching into field internals.
 *
 * Both are re-exported from FilterField.tsx as part of the public filter API.
 */

/** Icon + overline label used as a section header inside filter panels. */
export function SectionLabel({ icon, label }: { icon: React.ReactElement; label: string }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
      <Box
        sx={{
          width: 28,
          height: 28,
          borderRadius: '8px',
          bgcolor: theme => alpha(theme.palette.primary.main, 0.1),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Typography variant="overline" className="filter-section-label">
        {label}
      </Typography>
    </Box>
  );
}

/**
 * Small bold caption rendered above a filter field in panel variant.
 * Used by TextFilterInput, DateFilterInput, NumericFilterInput, BooleanFilterInput —
 * avoids duplicating the same Typography snippet across every field strategy.
 * Not rendered in column variant (fields handle the isColumn check before calling this).
 */
export function FieldLabel({ children }: { children: string }) {
  return (
    <Typography variant="caption" className="filter-field-label">
      {children}
    </Typography>
  );
}

/**
 * Subtle italic hint rendered under a filter field (panel variant), e.g. the
 * "N values present in the list" availability note. Shared so any field strategy
 * can surface a helper line with the same tokenised styling
 * (`--fp-caption-font-size` / `--fp-label-color`).
 */
export function FieldHint({ children }: { children: React.ReactNode }) {
  return (
    <Typography variant="caption" className="filter-field-hint">
      {children}
    </Typography>
  );
}

/**
 * Error caption rendered under a filter field (panel variant) when the entered
 * value is invalid (out-of-range number, incoherent date range, …). Same
 * placement as {@link FieldHint} but in the theme's error colour, with
 * `role="alert"` so assistive tech announces the rejection.
 */
export function FieldError({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      variant="caption"
      role="alert"
      sx={{ display: 'block', mt: 0.25, color: 'error.main', lineHeight: 1.3 }}
    >
      {children}
    </Typography>
  );
}
